import {
  Category,
  CategoryCursorConnection,
  CreateCouponOrderPlacementPayload,
  CreateRegistrationPayload,
  CreateSendMailPayload,
  Studio,
  StudioCursorConnection,
} from '@definitions/types/symfonyTypes'

/**
 * Parses a StudioCursorConnection response into an array of Studio objects
 * @param studios - The GraphQL cursor connection response
 * @returns Array of Studio objects, empty array if invalid or null
 */
export function parseStudiosResponse(
  studios: StudioCursorConnection | null | undefined
): Studio[] {
  if (!studios?.edges) {
    return []
  }

  return studios.edges
    .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
    .map((edge) => edge.node)
    .filter((node): node is NonNullable<typeof node> => node !== null)
}

/**
 * Parses a CategoryCursorConnection response into an array of Category objects
 * @param categories - The GraphQL cursor connection response
 * @returns Array of Category objects, empty array if invalid or null
 */
export function parseCategoriesResponse(
  categories: CategoryCursorConnection | null | undefined
): Category[] {
  if (!categories?.edges) {
    return []
  }

  return categories.edges
    .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
    .map((edge) => edge.node)
    .filter((node): node is NonNullable<typeof node> => node !== null)
}

/**
 * Parses a CreateSendMailPayload response
 * @param payload - The GraphQL mutation payload
 * @returns Parsed response with success status and optional error message
 * @throws Error if payload is invalid
 */
export function parseCreateSendMailResponse(
  payload: CreateSendMailPayload | null | undefined
): { success: boolean; error?: string } {
  if (!payload?.sendMail) {
    throw new Error('Invalid send mail response: sendMail is missing')
  }

  return {
    success: payload.sendMail.success,
    error: payload.sendMail.error || undefined,
  }
}

/**
 * Parses a CreateCouponOrderPlacementPayload response
 * @param payload - The GraphQL mutation payload
 * @returns Parsed response with status string or null
 * @throws Error if payload is invalid
 */
export function parseCreateCouponOrderResponse(
  payload: CreateCouponOrderPlacementPayload | null | undefined
): { status: string | null } {
  if (!payload?.couponOrderPlacement) {
    throw new Error(
      'Invalid coupon order response: couponOrderPlacement is missing'
    )
  }

  return {
    status: payload.couponOrderPlacement.status ?? null,
  }
}

/**
 * Parses a CreateRegistrationPayload response
 * @param payload - The GraphQL mutation payload
 * @returns Parsed response with registration ID
 * @throws Error if payload is invalid
 */
export function parseCreateRegistrationResponse(
  payload: CreateRegistrationPayload | null | undefined
): { id: string } {
  if (!payload?.registration) {
    throw new Error('Invalid registration response: registration is missing')
  }

  if (!payload.registration.id) {
    throw new Error('Invalid registration response: registration.id is missing')
  }

  return {
    id: payload.registration.id,
  }
}
